import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Pollutant } from "@/types/pollutant/pollutant.types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { PollutantForm } from "./pollutants/PollutantForm";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { usePollutantTable } from "@/hooks/pollutants/usePollutantTable";
import { usePollutantColumns } from "./pollutants/columns";
import { useConfirmationDialog } from "@/hooks/pollutants/useConfirmationDialog";

export const Pollutants = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedPollutant, setSelectedPollutant] = useState<Pollutant | null>(null);

  const {
    filteredPollutants,
    loading,
    refetch,
    searchTerm,
    setSearchTerm,
    nameFilters,
    setNameFilters,
    clearFilters,
    nameOptions,
  } = usePollutantTable();

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  } = useConfirmationDialog(refetch);

  const handleAddPollutant = () => {
    setSelectedPollutant(null);
    setFormOpen(true);
  };

  const handleEditPollutant = (pollutant: Pollutant) => {
    setSelectedPollutant(pollutant);
    setFormOpen(true);
  };

  const handleDeleteAction = (pollutant: Pollutant) => {
    openDialog({
      type: "delete",
      pollutantId: pollutant.id || "",
      pollutantName: pollutant.name,
    });
  };

  const columns = usePollutantColumns(t, handleEditPollutant, handleDeleteAction);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("pollutants.filters.name"),
      options: nameOptions,
      selectedValues: nameFilters,
      onChange: setNameFilters,
      showSearch: true,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("pollutants.title")}</h2>
        {!isAssimagraUser && (
          <Button
            onClick={handleAddPollutant}
            className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("pollutants.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("pollutants.searchPlaceholder")}
        filters={filters}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        <DataTable columns={columns} data={filteredPollutants} pageSize={10} className="w-full" minWidth="auto" />
      </div>

      <PollutantForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        pollutantToEdit={selectedPollutant}
        onSuccess={refetch}
      />

      {confirmAction && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={closeDialog}
          onConfirm={handleConfirm}
          title={t("pollutants.confirmDelete.title")}
          description={t("pollutants.confirmDelete.description")}
          confirmText={
            isConfirmActionLoading ? t("pollutants.confirmDelete.actionLoading") : t("pollutants.confirmDelete.action")
          }
          cancelText={t("pollutants.cancel")}
          variant="destructive"
          isLoading={isConfirmActionLoading}
        />
      )}
    </div>
  );
};
